import React, { useContext, useMemo, useState } from 'react';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import { Field, Form } from 'react-final-form';
import { Button, Loader } from 'ncoded-component-library';
import CheckToggleField from 'components/CheckToggleField';
import TextAreaField from 'components/TextAreaField';
import useCallbackAsync from 'hooks/useCallbackAsync';
import SelectLeads from '../SelectLeads';
import api from 'api';
import { BuyerEnquiryHistory } from 'models/DealRoom';
import {
  getArrayLength,
  getCheckedValuesInObject,
  getHasElementsInArray,
  getIsArrayEmpty,
} from 'utils';
import ListingContext from 'router/subrouters/Dash/subrouters/private/subrouters/Portfolio/subrouters/Seller/subrouters/ListingDetailSeller/providers/Listing/Listing.context';
import PopNotificationsContext from 'providers/PopNotifications/PopNotifications.context';
import useInfinitePagination from 'hooks/useInfinitePagination';
import { DEFAULT_LIMIT } from 'data/global';
import useLeadStagesCopy from 'hooks/useLeadStagesCopy';
import { LEAD_STAGES } from 'models/Lead';
import ResolveMultiSelect from 'components/ResolveMultiSelect';
import useDebounceSearch from 'hooks/useDebounceSearch';
import { getQueryArrayParam, SORT_VALUES } from '../../utils';
import useMatchMedia from 'hooks/useMatchMedia';
import useGetMixpanelProps from 'hooks/useGetMixpanelProps';
import {
  ENQUIRY_ORIGIN,
  EnquiryCreationInitiatedEventProps,
  MANUAL_ENQUIRY_SUBCATEGORY,
} from 'models/MixpanelEvents';
import mixpanelService from 'services/mixpanelService';

import './InviteLeadsForm.styles.scss';

type FormValues = {
  leads: Record<string, boolean>;
  includeMessage?: boolean;
  customMessage?: string;
};

type InviteLeadsFormProps = {
  className?: string;
  onClose: () => void;
};

const InviteLeadsForm: React.FC<InviteLeadsFormProps> = (props) => {
  const { className, onClose } = props;

  const [sort, setSort] = useState(`-${SORT_VALUES.NUMBER_OF_ENQUIRIES}`);
  const [dealRoomOptions, setDealRoomOptions] = useState([]);
  const [dealRoomIds, setDealRoomIds] = useState<string[]>([]);
  const [buyerSteps, setBuyerSteps] = useState<string[]>([]);

  const {
    listing: { _id: listingId },
    dealRoom: { _id: dealRoomId, name, leadStagesCopies },
    setEnquiry,
  } = useContext(ListingContext);

  const { popSuccess } = useContext(PopNotificationsContext);

  const getMixpanelProps = useGetMixpanelProps();

  const classes = classNames('invite-leads-form', className);

  const { t } = useTranslation();

  const isPhablet = useMatchMedia('isPhablet');

  const copies = useLeadStagesCopy().getCopies(leadStagesCopies);

  const leadStageOptions = useMemo(
    () =>
      Object.values(LEAD_STAGES).map((value, index) => ({
        value,
        label: `${copies[value]} (${t('stage')} ${index + 1})`,
      })),
    [copies, t],
  );

  const [onSearch, isSearchingDeals] = useCallbackAsync(
    async (searchString: string) => {
      if (!searchString) {
        setDealRoomOptions([]);
        return;
      }

      const { data } = await api.portfolio.seller.searchDeals(
        dealRoomId,
        searchString,
      );

      setDealRoomOptions(
        data.map(({ _id, name }) => ({ value: _id, label: name })),
      );
    },
  );

  const { isDebouncing, onSearchInputChange } = useDebounceSearch({ onSearch });

  const isSearchingDealsWithDebounce = isSearchingDeals || isDebouncing;

  const { loading, items, totalItems, onContainerScrolled } =
    useInfinitePagination<BuyerEnquiryHistory>({
      uniqueKey: 'buyerEmail',
      condition: !!dealRoomId,
      resetItemsWhenReset: false,
      resetDeps: [dealRoomId, dealRoomIds, buyerSteps, sort],
      dependencies: [dealRoomId, dealRoomIds, buyerSteps, sort],
      makeRequest: async (currentPage) =>
        api.dealRoom
          .getPreviousDealsLeads(dealRoomId, {
            $page: currentPage.toString(),
            $limit: DEFAULT_LIMIT,
            $sort: sort,
            dealRoomIds: getQueryArrayParam(dealRoomIds),
            buyerSteps: getQueryArrayParam(buyerSteps),
          })
          .then(({ data }) => data),
    });

  const itemsHasElements = getHasElementsInArray(items);

  const [onSubmit] = useCallbackAsync(async (values: FormValues) => {
    const { leads, includeMessage, customMessage } = values;

    const buyerIds = getCheckedValuesInObject(leads);

    if (getIsArrayEmpty(buyerIds)) return;

    buyerIds.forEach((_) => {
      mixpanelService.enquiryCreationInitiated(
        getMixpanelProps({
          dealId: dealRoomId,
          dealName: name,
          enquiryOrigin: ENQUIRY_ORIGIN.MANUAL,
          manualEnquirySubcategory: MANUAL_ENQUIRY_SUBCATEGORY.PREVIOUS_LEAD,
        }) as EnquiryCreationInitiatedEventProps,
      );
    });

    const { data } = await api.portfolio.seller.addPreviousLead(listingId, {
      buyerIds,
      ...(includeMessage && customMessage && { customMessage }),
    });

    popSuccess(
      t('successfullyInvitedLeads', { count: getArrayLength(buyerIds) }),
    );
    setEnquiry(data);
    onClose();
  });

  const isEmpty =
    !loading &&
    !itemsHasElements &&
    !getHasElementsInArray(dealRoomIds) &&
    !getHasElementsInArray(buyerSteps);

  if (loading && !itemsHasElements) return <Loader />;

  if (isEmpty) return <p className="empty-leads">{t('noLeadsFromDeal')}</p>;

  return (
    <Form
      onSubmit={onSubmit}
      render={({
        submitting,
        invalid,
        values: { includeMessage },
        handleSubmit,
      }) => (
        <form onSubmit={handleSubmit} className={classes}>
          {(loading || submitting) && <Loader />}

          <p>{t('inviteLeadsFromDealsDescription', { dealRoomName: name })}</p>

          <div className="invite-leads-form__filter">
            <p>{t('filterLeadsByDealsTheyEnquired')}</p>
            <ResolveMultiSelect
              value={dealRoomIds}
              options={dealRoomOptions}
              hasCheckbox
              renderAsPortal={!isPhablet}
              loading={isSearchingDealsWithDebounce}
              noDataTitle={t('typeToViewAvailableOptions')}
              placeholder={t('selectDeal')}
              emptySearchTitle={t('foundNoDeals')}
              onSearchChange={onSearchInputChange}
              onChange={setDealRoomIds}
            />
          </div>

          <div className="invite-leads-form__filter">
            <p>{t('filterLeadsByTheirLatestStage')}</p>
            <ResolveMultiSelect
              value={buyerSteps}
              searchable={false}
              hasCheckbox
              renderAsPortal={!isPhablet}
              placeholder={t('selectStage')}
              options={leadStageOptions}
              onChange={setBuyerSteps}
            />
          </div>

          {itemsHasElements ? (
            <>
              <p className="displayed-leads">
                {t('displayedLeads', { count: totalItems })}
              </p>
              <SelectLeads
                leads={items}
                sort={sort}
                updateSort={setSort}
                onContainerScroll={onContainerScrolled}
              />
              <Field
                name="includeMessage"
                type="checkbox"
                label={t('includeMessage')}
                component={CheckToggleField}
              />
              {includeMessage && (
                <Field
                  name="customMessage"
                  component={TextAreaField}
                  rows={3}
                />
              )}
            </>
          ) : (
            <p className="empty-leads">{t('noLeadsFromDeal')}</p>
          )}

          <div className="actions">
            <Button variant="link" onClick={onClose}>
              {t('cancel')}
            </Button>

            <Button
              type="submit"
              disabled={submitting || invalid || !itemsHasElements}
            >
              {t('addEnquiries')}
            </Button>
          </div>
        </form>
      )}
    />
  );
};

export default InviteLeadsForm;
